import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Skeleton } from '@mui/material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import octopusInstance from '../../../services/octopus/octopusInstance';
import { searchInstrument } from '../../../services/backendAPI';
import { findNearestFuture, formatExpiry } from '../utils/expiryUtils';

// Exchange mapping based on user provided config
const exchangeCodes = {
  "NSE": 1,
  "NFO": 2,
  "CDS": 3,
  "MCX": 4,
  "MCXSX": 5,
  "BSE": 6,
  "BFO": 7,
  "BCD": 8,
  "NSE_INDICES": 9,
  "NCDEX": 10,
  "NCOM": 11,
  "NSLBM": 12,
  "NOFS": 13,
  "DSE": 14,
  "BSLBM": 15
};

// Helper component for the Logo Avatar
const StockLogo = ({ symbol }) => {
  const shortName = symbol.slice(0, 2).toUpperCase();
  return (
    <Box 
      sx={(theme) => ({ 
        width: 40, 
        height: 40, 
        borderRadius: 1, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        fontWeight: 'bold',
        color: theme.palette.error.main,
        border: `1px solid ${theme.palette.divider}`
      })}
    >
      {shortName}
    </Box>
  );
};

const DashboardCommonCard = ({ 
  symbol: propSymbol, 
  name: propName, 
  price: propPrice, 
  change: propChange, 
  pct: propPct, 
  expiry: propExpiry, 
  active, 
  onClick, 
  token: propToken, 
  wsCode: propWsCode,
  useNearestFuture = false,
  layout = 'default',
  hidePercentChange = false,
  onContractLoaded
}) => {
  const [wsData, setWsData] = useState({ price: propPrice || 0, change: propChange || 0, pct: propPct || 0 });
  const [contractInfo, setContractInfo] = useState(null);
  const [loading, setLoading] = useState(useNearestFuture);

  useEffect(() => {
    if (useNearestFuture && propSymbol) {
      setLoading(true);
      const searchQuery = propSymbol.replace(/\s+/g, '');
      
      searchInstrument(searchQuery).then(res => {
        const results = Array.isArray(res) ? res : (res?.result || res?.data || []);
        const nearest = findNearestFuture(results, searchQuery);
        if (nearest) {
          setContractInfo(nearest);
          if (onContractLoaded) onContractLoaded(nearest);
        }
        setLoading(false);
      }).catch(() => setLoading(false));
    }
  }, [propSymbol, useNearestFuture]);

  const token = useNearestFuture ? contractInfo?.token : propToken;
  const symbol = useNearestFuture ? contractInfo?.trading_symbol : propSymbol;
  const expiry = useNearestFuture ? formatExpiry(contractInfo?.expiry) : propExpiry;
  const name = useNearestFuture ? propSymbol : propName;
  
  const wsCode = useNearestFuture 
    ? (exchangeCodes[contractInfo?.exchange] || 2) 
    : (propWsCode || 1);

  useEffect(() => {
    if (!token) return;

    const handler = octopusInstance.wsHandler({
      messageType: 'DetailMarketDataMessage',
      subscriptionLocation: `dashboard-card-${token}`,
      payload: { exchangeCode: wsCode, instrumentToken: Number(token) },
    });

    handler.subscribe(({ msg }) => {
      const price = msg.lastTradedPrice ?? msg.ltp;
      if (price) {
        setWsData({
          price: price,
          change: msg.absoluteChange ?? 0,
          pct: msg.percentChange ?? 0
        });
      }
    });

    return () => handler.unsubscribe();
  }, [token, wsCode]);

  const ltp = token ? wsData.price : (propPrice || 0);
  const absChange = token ? wsData.change : (propChange || 0);
  const pctChange = token ? wsData.pct : (propPct || 0);
  
  const ltpValue = parseFloat(ltp);
  const isPositive = absChange >= 0;

  if (loading) {
    if (layout === 'fno') {
      return (
        <Paper elevation={0} sx={{ minWidth: { xs: 150, sm: 180, lg: 200 }, maxWidth: 240, p: { xs: 1.5, lg: 2 }, border: '1px solid #E0E0E0', borderRadius: 2, height: { xs: 88, lg: 100 }, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <Skeleton variant="text" width="40%" height={20} />
          <Skeleton variant="text" width="70%" height={32} />
          <Skeleton variant="text" width="50%" height={16} />
        </Paper>
      );
    }
    return (
      <Paper elevation={0} sx={{ minWidth: { xs: 180, sm: 200, lg: 220 }, maxWidth: 240, p: { xs: 1.5, lg: 2 }, border: '1px solid #E0E0E0', borderRadius: 2, height: { xs: 100, lg: 120 } }}>
        <Skeleton variant="rectangular" width={40} height={40} />
        <Skeleton variant="text" sx={{ mt: 1 }} width="60%" />
        <Skeleton variant="text" width="40%" />
        <Skeleton variant="text" sx={{ mt: 2 }} width="80%" />
      </Paper>
    );
  }


  // Layout for F&O Tabs (Name, Price, Change stacked)
  if (layout === 'fno') {
    return (
      <Paper 
        elevation={0}
        onClick={onClick}
        sx={{ 
          minWidth: { xs: 150, sm: 180, lg: 200 },
          maxWidth: 240,
          p: { xs: 1.5, lg: 2 }, 
          border: '1px solid',
          borderColor: active ? 'primary.main' : '#E0E0E0',
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          height: { xs: 88, lg: 100 }, 
          overflow: 'hidden',
          position: 'relative',
          transition: 'all 0.2s',
          cursor: onClick ? 'pointer' : 'default',
          backgroundColor: active ? 'rgba(25, 118, 210, 0.04)' : 'background.paper',
          '&:hover': {
             boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
             borderColor: active ? 'primary.main' : 'text.secondary',
             transform: onClick ? 'translateY(-2px)' : 'none',
          }
        }}
      >
        {/* 1st row: Name */}
        <Typography variant="subtitle2" fontWeight="700" noWrap sx={{ lineHeight: 1.5, fontSize: { xs: '0.75rem', sm: '0.8rem', lg: '0.875rem' } }}>
          {name || symbol}
        </Typography>
        
        {/* 2nd row: Price */}
        <Typography variant="body1" noWrap sx={{ mt: 0.5, color: 'text.secondary', fontWeight: 500, fontSize: { xs: '0.85rem', sm: '0.95rem', lg: '1.1rem' } }}>
          ₹{ltpValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </Typography>
        
        {/* 3rd row: Change and % */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto', width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {isPositive ? (
              <ArrowDropUpIcon sx={{ color: 'success.main', fontSize: 18, ml: -0.5 }} />
            ) : (
              <ArrowDropDownIcon sx={{ color: 'error.main', fontSize: 18, ml: -0.5 }} />
            )}
            <Typography 
              variant="caption" 
              fontWeight="600"
              color={isPositive ? 'success.main' : 'error.main'}
              sx={{ fontSize: { xs: '0.6rem', lg: '0.7rem' } }}
            >
              ₹{Math.abs(absChange).toFixed(2)}
            </Typography>
          </Box>
          
          {!hidePercentChange && (
            <Typography 
              variant="caption" 
              fontWeight="600"
              color={isPositive ? 'success.main' : 'error.main'}
              sx={{ fontSize: { xs: '0.6rem', lg: '0.7rem' } }}
            >
              ({isPositive ? '+' : ''}{pctChange.toFixed(2)}%)
            </Typography>
          )}
        </Box>
      </Paper>
    );
  }

  // Default layout (Most Active Stocks style)
  return (
    <Paper 
        elevation={0}
        onClick={onClick}
        sx={{ 
          minWidth: { xs: 180, sm: 200, lg: 220 }, 
          maxWidth: 240,
          p: { xs: 1.5, lg: 2 }, 
          border: '1px solid',
          borderColor: active ? 'primary.main' : '#E0E0E0',
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          height: { xs: 100, lg: 120 }, 
          overflow: 'hidden',
          position: 'relative',
          transition: 'all 0.2s',
          cursor: onClick ? 'pointer' : 'default',
          backgroundColor: active ? 'rgba(25, 118, 210, 0.04)' : 'background.paper',
          '&:hover': {
             boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
             borderColor: active ? 'primary.main' : 'text.secondary',
             transform: onClick ? 'translateY(-2px)' : 'none',
          }
        }}
      >
      {/* Top Row: Logo & Name */}
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <StockLogo symbol={symbol || 'UNK'} />
        <Box sx={{ overflow: 'hidden' }}>
          <Typography variant="subtitle2" fontWeight="700" noWrap>
            {symbol}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', fontSize: '0.65rem' }}>
            {name}
          </Typography>
          {expiry && (
            <Typography variant="caption" color="text.primary" sx={{ display: 'block', fontSize: '0.65rem', fontWeight: 600, mt: 0.5 }}>
              {expiry}
            </Typography>
          )}
        </Box>
      </Box>
      
      {/* Bottom Row: Price & Change */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
        <Typography variant="subtitle2" fontWeight="700" noWrap sx={{ fontSize: { xs: '0.8rem', lg: '0.875rem' } }}>
          ₹{ltpValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {isPositive ? (
              <ArrowDropUpIcon sx={{ color: 'success.main', fontSize: 18 }} />
            ) : (
              <ArrowDropDownIcon sx={{ color: 'error.main', fontSize: 18 }} />
            )}
            <Typography 
              variant="caption" 
              fontWeight="600"
              color={isPositive ? 'success.main' : 'error.main'}
              sx={{ fontSize: '0.65rem' }}
            >
              {isPositive ? '+' : ''}₹{Math.abs(absChange).toFixed(2)}
            </Typography>
          </Box>
          {!hidePercentChange && (
            <Typography 
              variant="caption" 
              fontWeight="600"
              color={isPositive ? 'success.main' : 'error.main'}
              sx={{ fontSize: '0.65rem', mr: 0.5 }} 
            >
              ({isPositive ? '+' : ''}{pctChange.toFixed(2)}%)
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default DashboardCommonCard;
